import {
  IdentityProvider,
  OauthAttemptTestStatus,
  SSOAuthorizationURLResponseMode,
} from "@coeff/api-admin-ext";
import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { useEffect, useState } from "react";

import { QueryCacheKey } from "../api";
import { sentryCapture, useApiContext } from "../utils";

type OauthState = {
  isAuthInProgress: boolean;
  isAuthCompleted: boolean;
  ssoConfigTestId: string | undefined;
  authTab: Window | null;
};

export const useOAuth = () => {
  const { adminExtApiClient: api } = useApiContext();

  const [state, setState] = useState<OauthState>({
    isAuthInProgress: false,
    isAuthCompleted: false,
    ssoConfigTestId: undefined,
    authTab: null,
  });

  useEffect(() => {
    return () => {
      state.authTab?.close();
    };
  }, [state.authTab]);

  const oauthAttemptMutation = useMutation({
    mutationFn: async ({
      clientId,
      clientSecret,
      issuerUrl,
      idProvider,
      responseModeOverride,
    }: {
      clientId: string;
      clientSecret: string;
      issuerUrl: string;
      idProvider: IdentityProvider;
      responseModeOverride?: SSOAuthorizationURLResponseMode;
    }) => {
      const oauthAttemptTestData = (
        await api.createOauthAttemptTest({
          create_oauth_attempt_test_request: {
            client_id: clientId,
            client_secret: clientSecret,
            issuer_url: issuerUrl,
            id_provider: idProvider,
            response_mode_override: responseModeOverride,
          },
        })
      ).data;

      const oauthAttemptTest = oauthAttemptTestData.oauth_attempt_test;
      const authorizationUrl = oauthAttemptTestData.authorization_url;

      return { oauthAttemptTest, authorizationUrl };
    },
    onSuccess: ({ oauthAttemptTest, authorizationUrl }) => {
      setState(prev => ({
        ...prev,
        ssoConfigTestId: oauthAttemptTest?.sso_config_test_id,
      }));

      const newTab = window.open(authorizationUrl, "_blank");
      newTab?.focus();

      setState(prev => ({
        ...prev,
        authTab: newTab,
      }));
    },
    onError: (error: Error) => {
      state.authTab?.close();
      sentryCapture({
        error,
        screen: "Admin SSO Config: Test SSO Config",
        name: "Test SSO Config: POST /api/sso_config/start_test",
      });
      message.error("Error initializing connection. Try again.");
      setState(prev => ({ ...prev, isAuthInProgress: false }));
    },
  });

  const { data: oauthAttemptResponse } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT_TEST, state.ssoConfigTestId],
    () => api.getOauthAttemptStatus({ sso_config_test_id: state.ssoConfigTestId! }),
    {
      enabled: Boolean(state.isAuthInProgress && state.ssoConfigTestId),
      refetchInterval: response =>
        response?.data.oauth_attempt_test.status !== OauthAttemptTestStatus.InProgress
          ? false
          : 2_000,
      refetchIntervalInBackground: true,
      retry: 3,
    }
  );

  useEffect(() => {
    if (oauthAttemptResponse?.data?.oauth_attempt_test.status === "success") {
      state.authTab?.close();
      setState(prev => ({ ...prev, isAuthInProgress: false, isAuthCompleted: true }));
      message.success("Validated successfully");
    } else if (oauthAttemptResponse?.data.oauth_attempt_test.status === "failure") {
      state.authTab?.close();
      setState(prev => ({ ...prev, isAuthInProgress: false }));
      message.error(
        oauthAttemptResponse?.data.oauth_attempt_test.error_message ?? "Validation failed"
      );
    }
  }, [oauthAttemptResponse, state.authTab]);

  const handleAuthorize = async ({
    clientId,
    clientSecret,
    issuerUrl,
    idProvider,
    responseModeOverride,
  }: {
    clientId: string;
    clientSecret: string;
    issuerUrl: string;
    idProvider: IdentityProvider;
    responseModeOverride?: SSOAuthorizationURLResponseMode;
  }) => {
    setState(prev => ({ ...prev, isAuthInProgress: true }));

    await oauthAttemptMutation.mutateAsync({
      clientId: clientId,
      clientSecret: clientSecret,
      issuerUrl: issuerUrl,
      idProvider: idProvider,
      responseModeOverride: responseModeOverride,
    });
  };

  const handleCancel = () => {
    setState(prev => ({ ...prev, isAuthInProgress: false }));
  };

  return {
    isAuthCompleted: state.isAuthCompleted,
    isAuthInProgress: state.isAuthInProgress,
    error: oauthAttemptMutation.error,
    handleAuthorize,
    handleCancel,
  };
};
